import { Flex } from "@/components/layout/flexbox";
import { Clickable } from "@/components/pieces/interaction/Clickable";
import { ProductDropdown } from "@/components/pieces/products/ProductDropdown";
import { ProductItem } from "@/components/pieces/products/ProductItem";
import {
  Specialty,
  CategoryProduct,
  MeetingOrReviewProduct,
  BitProduct,
  CreditProduct,
} from "@/types/products";
import { Resolve } from "@/types/utils";
import { parseDecimal } from "@/utils/string";

const NUMBER_OF_PACKAGES = 8;

type ProductType = Resolve<
  (MeetingOrReviewProduct | CategoryProduct | BitProduct | CreditProduct) & {
    locked?: boolean | null;
    creditValue?: string | null;
  }
>;

interface ProductProps {
  product: ProductType;
  selectedSpecialty?: Specialty | null;
  selected: boolean;
  disabled: boolean;
  onClick: () => void;
  width: number;
  maxPerRow?: number;
  hideDisabled?: boolean;
}

function Product({
  product,
  selectedSpecialty,
  selected,
  disabled,
  onClick,
  width,
  maxPerRow = 4,
  hideDisabled = false,
}: ProductProps) {
  let flex: string[] = [];
  let maxWidth: string | string[] = "";
  if (maxPerRow === 1) {
    flex = ["1 1 100%"];
    maxWidth = "100%";
  } else if (maxPerRow === 2) {
    flex = ["1 1 100%", "1 1 50%"];
    maxWidth = ["100%", "50%"];
  } else if (maxPerRow === 3) {
    flex = ["1 1 100%", "1 1 33%"];
    maxWidth = ["100%", "33%"];
  } else if (maxPerRow >= 4) {
    flex = ["1 1 100%", "1 1 33%", "1 1 33%", "1 1 25%"];
    maxWidth = ["100%", "33%", "33%", "25%"];
  }

  return (
    <Clickable onClick={disabled ? undefined : onClick}>
      <Flex width={[1, 1, width]} flex={flex} maxWidth={maxWidth} mb={3}>
        <ProductItem
          disabled={disabled}
          product={product}
          selected={selected}
          selectedSpecialty={selectedSpecialty}
          hideDisabled={hideDisabled}
        />
      </Flex>
    </Clickable>
  );
}

interface ProductListProps {
  eligibleProducts: ProductType[];
  disabledProductIds?: string[];
  selectedProduct?: ProductType;
  setSelectedProduct: (product: ProductType) => void;
  disabled: boolean;
  products: ProductType[];
  selectedSpecialty?: Specialty | null;
  minCredits?: string;
  maxPerRow?: number;
  hideDisabled?: boolean;
}

export function ProductList({
  eligibleProducts,
  disabledProductIds = [],
  selectedProduct,
  setSelectedProduct,
  disabled,
  products,
  selectedSpecialty,
  minCredits,
  maxPerRow = 4,
  hideDisabled = false,
}: ProductListProps) {
  const displayNumber =
    eligibleProducts.length > NUMBER_OF_PACKAGES
      ? NUMBER_OF_PACKAGES - 1
      : NUMBER_OF_PACKAGES;

  const options = eligibleProducts
    .map((p) => ({
      ...p,
      disabled: disabledProductIds.includes(p.id),
    }))
    // Sort disabled products to the end
    .sort((x, y) => (x.disabled === y.disabled ? 0 : x.disabled ? 1 : -1));

  const defaultOption = options.slice(0, displayNumber);
  const moreOptions = options.slice(displayNumber, options.length);
  const minCreditsNumber = parseDecimal(minCredits);

  return (
    <>
      {defaultOption.map((product) => {
        const { creditValue = "0.00", locked } = product;
        const creditValueNumber = parseDecimal(creditValue);

        const disabledProduct =
          disabled ||
          creditValueNumber < minCreditsNumber ||
          locked ||
          disabledProductIds.includes(product.id);

        return (
          <Product
            key={product.id}
            product={product}
            selected={
              selectedProduct ? product.id === selectedProduct.id : false
            }
            onClick={() => setSelectedProduct(product)}
            disabled={disabledProduct}
            width={products.length > 0 ? 1 / products.length : 1}
            selectedSpecialty={selectedSpecialty}
            maxPerRow={maxPerRow}
            hideDisabled={hideDisabled}
          />
        );
      })}
      {moreOptions.length > 0 && (
        <ProductDropdown
          disabled={disabled}
          options={moreOptions}
          selectedId={selectedProduct?.id || ""}
          onChange={setSelectedProduct}
          hideDisabled={hideDisabled}
        />
      )}
    </>
  );
}
